import React, { useEffect, useRef, useState } from 'react';
import CustomActionButton from '../ui/buttons/CustomActionButton';
import RetirementProjectionChart from '../ui/charts/RetirementProjectionChart';
import CustomCheckbox from '../ui/checkboxes/CustomCheckbox';
import DreamInsight from '../ui/labels/DreamInsight';

const RetirementScenarioCard = ({ retirementScenarios, retirementChartData, onEdit }) => {
  const [visibleScenarioIds, setVisibleScenarioIds] = useState([]);
  const initialized = useRef(false);

  const scenarioColors = [
    '#3084B2',
    '#2FA2A6',
    '#2D536E',
    '#48a6c7',
    '#8DB8B9',
    '#238B8F',
    '#7d47c7',
    '#c7484a',
    '#4847c7',
    '#c78548',
    '#c7b448',
    '#48c7b8',
    '#9b48c7',
    '#c748a3',
  ];
  const scenarioColorMap = {};
  retirementScenarios.forEach((s, idx) => {
    scenarioColorMap[s.id] = scenarioColors[idx % scenarioColors.length];
  });

  useEffect(() => {
    if (!initialized.current && retirementScenarios.length > 0) {
      setVisibleScenarioIds([retirementScenarios[0].id]);
      initialized.current = true;
    }
  }, [retirementScenarios]);

  const toggleScenario = (id) => {
    setVisibleScenarioIds((prev) => (prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]));
  };

  const visibleData = retirementChartData.filter((scenario) => visibleScenarioIds.includes(scenario.scenarioId));

  return (
    <div className="bg-white rounded-custom20 flex flex-col gap-4 col-span-3">
      <div className="flex flex-col bg-blueHighlight rounded-t-custom20">
        <div className="flex justify-between items-center px-6 py-4">
          <h3 className="text-white text-lg font-semibold">Independência Financeira</h3>
          <div className="flex gap-2">
            <CustomActionButton className="h-7" state="alt" onClick={onEdit}>
              VER CENÁRIOS
            </CustomActionButton>
          </div>
        </div>
      </div>

      <div className="px-6 pb-6 flex flex-col gap-4">
        {retirementScenarios.length > 0 ? (
          <>
            {/* Checkboxes de seleção de cenário */}
            <div className="flex flex-wrap gap-4 justify-center">
              {retirementScenarios.map((scenario) => (
                <label key={scenario.id} className="flex items-center gap-2 cursor-pointer text-sm text-blueDark">
                  <CustomCheckbox
                    name={`scenario-${scenario.id}`}
                    checked={visibleScenarioIds.includes(scenario.id)}
                    onChange={() => toggleScenario(scenario.id)}
                    className="text-bluePrimary"
                    label={scenario.name || `Cenário ${scenario.id}`}
                  />
                </label>
              ))}
            </div>

            {/* Gráfico com os cenários visíveis */}
            <div className="w-full">
              <RetirementProjectionChart scenarios={visibleData} scenarioColorMap={scenarioColorMap} />
            </div>

            <div className="flex flex-col gap-6 mt-4">
              {visibleData.map((scenario) => {
                const { scenarioId, scenarioName, insights } = scenario;

                const insightTypes = ['ok', 'problem', 'warning'];

                const hasInsights = insightTypes.some((type) => insights?.[type]?.length > 0);
                if (!hasInsights) return null;

                return (
                  <div key={scenarioId} className="flex flex-col gap-2">
                    <span className="text-base font-semibold" style={{ color: scenarioColorMap[scenarioId] }}>
                      {scenarioName || `Cenário ${scenarioId}`}
                    </span>

                    {insightTypes.map((type) => {
                      const items = insights?.[type] || [];
                      if (items.length === 0) return null;

                      return (
                        <div key={type} className="flex flex-col gap-1">
                          {items.map((text, idx) => (
                            <DreamInsight key={`${scenarioId}-${type}-${idx}`} type={type} text={text} />
                          ))}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-4 px-6 mb-6">
            <span className="text-base text-blueSteelDark">Nenhum cenário configurado</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default RetirementScenarioCard;
